import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';

const createExpression = () => /is (\d*)(\D*)./gm;

interface Booking {
    id: number;
    guestName: string;
    checkin: string;
    checkout: string;
}

interface LockboxCode {
    id: number;
    name: string;
    code: string;
    from: string;
    to: string;
}

const LockboxCodeDialog = ({
    bookingId,
    onFocusLost,
}: {
    bookingId: number;
    onFocusLost: () => void;
}) => {
    const [booking, setBooking] = useState<Booking | null>(null);
    const [lockboxCode, setLockboxCode] = useState<LockboxCode | null>(null);
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadBooking = useCallback(async () => {
        try {
            const response = await apiRawFetch(
                `api/bookings/${bookingId}`,
                'get'
            );
            if (!response.ok) {
                throw new Error('Failed to fetch booking');
            }
            const bookingData: Booking = await response.json();
            setBooking(bookingData);
            return bookingData;
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to load booking'
            );
            return null;
        }
    }, [bookingId]);

    const loadLockboxCode = useCallback(
        async (checkin: string, checkout: string) => {
            try {
                const response = await apiRawFetch(
                    `api/lockbox-codes/active?checkin=${encodeURIComponent(checkin)}&checkout=${encodeURIComponent(checkout)}`,
                    'get'
                );
                if (!response.ok) {
                    if (response.status === 404) {
                        setLockboxCode(null);
                        return null;
                    }
                    throw new Error('Failed to fetch lockbox code');
                }
                const lockboxCodeData: LockboxCode = await response.json();
                setLockboxCode(lockboxCodeData);
                return lockboxCodeData;
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load lockbox code'
                );
                return null;
            }
        },
        []
    );

    const updateBooking = useCallback(
        async (lockboxCodeValue: string) => {
            try {
                setUpdateLoading(true);
                const response = await apiRawFetch(
                    `api/bookings/${bookingId}`,
                    'post',
                    {
                        lockboxCode: lockboxCodeValue,
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to update booking');
                }
                return true;
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to update booking'
                );
                return false;
            } finally {
                setUpdateLoading(false);
            }
        },
        [bookingId]
    );

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);

            const bookingData = await loadBooking();
            if (bookingData) {
                await loadLockboxCode(
                    bookingData.checkin,
                    bookingData.checkout
                );
            }

            setLoading(false);
        };

        loadData();
    }, [loadBooking, loadLockboxCode]);

    useEffect(() => {
        if (lockboxCode && !value) {
            setValue(lockboxCode.code);
        }
    }, [lockboxCode, value]);

    if (loading || updateLoading) {
        return <SpinnerIcon />;
    }

    if (error) {
        return (
            <Dialog open>
                <DialogContent>
                    <div>Error: {error}</div>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={onFocusLost}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    if (!booking) {
        return (
            <Dialog open>
                <DialogContent>
                    <div>Booking not found</div>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={onFocusLost}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open>
            <DialogContent>
                <div>Name: {booking.guestName}</div>
                <div>Checkin: {booking.checkin}</div>
                <div>Checkout: {booking.checkout}</div>
                {lockboxCode && (
                    <div>Found lockbox code: {lockboxCode.name}</div>
                )}
                <TextField
                    value={value}
                    onChange={event => {
                        setValue(event.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Box display="flex" gap={2}>
                    <Button variant="contained" onClick={onFocusLost}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!value}
                        onClick={async () => {
                            const success = await updateBooking(value);
                            if (success) {
                                onFocusLost();
                            }
                        }}
                    >
                        Set lockbox code
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

const LockboxCodeForm = ({
    bookingId,
    onClose,
}: {
    bookingId: number;
    onClose(): void;
}) => {
    return <LockboxCodeDialog bookingId={bookingId} onFocusLost={onClose} />;
};

export default LockboxCodeForm;
